import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useArtifactStorageApi, useFinanceApi, useRecruitmentApi, useDeliveryApi } from 'infrastructure/api';
import { HTTP_METHOD } from 'framework/api';
import { appendArtifacts, removeArtifacts, clearArtifacts } from './artifactsActions';
import { storageService } from 'framework/utils';
import { useBaseCrudService } from 'framework/crud';
import { getFilePreview, getFileTypeIcon, isLinkExpired, mapToFileData } from './utils';
import { useParams } from 'react-router-dom';
import useArtifactsInfoReceiver from './receivers/useArtifactsInfoReceiver';
import useCandidateArtifactsInfoReceiver from './receivers/useCandidateArtifactsInfoReceiver';
import useInvoiceArtifactsInfoReceiver from './receivers/useInvoiceArtifactsInfoReceiver';
import useProjectArtifactsInfoReceiver from './receivers/useProjectArtifactsInfoReceiver';
import useRiskArtifactsInfoReceiver from './receivers/useRiskArtifactsInfoReceiver';
import useStaffingArtifactsInfoReceiver from './receivers/useStaffingArtifactsInfoReceiver';
import useBudgetArtifactsInfoReceiver from './receivers/useBudgetArtifactsInfoReceiver';
import { buildArtifactServiceConfig } from './artifactServiceConfig';
import {
  ArtifactData,
  ArtifactIdentifier,
  ArtifactMap,
  ArtifactStoreState,
  BlobResponse,
  JsonResponse,
  SendGetRequest,
  UploadFileParams,
  UploadFileResponse,
} from './types';

type ArtifactServiceOptions = {
  uploadFile?: (params: UploadFileParams) => Promise<UploadFileResponse>;
  isCandidateService?: boolean;
  isProjectService?: boolean;
  isRiskService?: boolean;
  isStaffingService?: boolean;
  budgetId?: StringOrNumber;
};

const useArtifactService = ({
  uploadFile: uploadFileFromProps,
  isCandidateService,
  isProjectService,
  isRiskService,
  isStaffingService,
  budgetId,
}: ArtifactServiceOptions = {}) => {
  const { riskId: routeRiskId } = useParams();

  const { collectArtifactsData } = useArtifactsInfoReceiver();
  const { collectCandidateArtifactsData } = useCandidateArtifactsInfoReceiver();
  const { collectInvoiceArtifactsData } = useInvoiceArtifactsInfoReceiver();
  const { hookLoading: isProjectArtifactsInfoReceiverLoading, collectProjectArtifactsData } =
    useProjectArtifactsInfoReceiver();
  const { hookLoading: isRiskArtifactsInfoReceiverLoading, collectRiskArtifactsData } =
    useRiskArtifactsInfoReceiver(routeRiskId);
  const { hookLoading: isStaffingArtifactsInfoReceiverLoading, collectStaffingArtifactsData } =
    useStaffingArtifactsInfoReceiver();
  const { hookLoading: isBudgetArtifactsInfoReceiverLoading, collectBudgetArtifactsData } =
    useBudgetArtifactsInfoReceiver();

  const dispatch = useDispatch();
  const artifacts = useSelector((state: ArtifactStoreState) => state.artifacts, shallowEqual);
  const { sendGetRequest: sendGetKernelRequest, uploadFile } = useArtifactStorageApi();
  const { sendGetRequest: sendFinanceGetRequest } = useFinanceApi();
  const { sendGetRequest: sendRecruitmentGetRequest } = useRecruitmentApi();
  const { sendGetRequest: sendDeliveryGetRequest } = useDeliveryApi();
  const kernelGetRequest = sendGetKernelRequest as SendGetRequest;
  const financeGetRequest = sendFinanceGetRequest as SendGetRequest;
  const { sendGetRequest, collectArtifactsDataAction, isArtifactsInfoReceiverLoading, buildArtifactUrl, uploadUrl } =
    buildArtifactServiceConfig({
      isCandidateService,
      isProjectService,
      isRiskService,
      isStaffingService,
      routeRiskId,
      budgetId,
      sendGetKernelRequest: kernelGetRequest,
      sendGetRecruitmentRequest: sendRecruitmentGetRequest as SendGetRequest,
      sendGetDeliveryRequest: sendDeliveryGetRequest as SendGetRequest,
      collectArtifactsData,
      collectCandidateArtifactsData,
      collectProjectArtifactsData,
      collectRiskArtifactsData,
      collectStaffingArtifactsData,
      collectBudgetArtifactsData: ids => collectBudgetArtifactsData(budgetId!, ids),
      isBudgetArtifactsInfoReceiverLoading,
      isProjectArtifactsInfoReceiverLoading,
      isRiskArtifactsInfoReceiverLoading,
      isStaffingArtifactsInfoReceiverLoading,
    });

  const getById = async (id: ArtifactIdentifier) => {
    const response = (await kernelGetRequest(`/artifacts/${id}/preview`)) as JsonResponse;
    return response.json();
  };

  const getPermissions = async (id: ArtifactIdentifier) => {
    try {
      const url = `/artifacts/${id}/permissions`;
      const response = (await kernelGetRequest(url)) as JsonResponse<unknown[]>;
      return response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('useArtifactService: getPermissions: Failed to fetch global permissions:', error);
      return [];
    }
  };

  const setArtifactsToStore = (artifactsData: ArtifactMap) => dispatch(appendArtifacts(artifactsData));

  const removeArtifactsFromStore = (artifactIds: ArtifactIdentifier[]) => dispatch(removeArtifacts(artifactIds));

  const getArtifactsDataByIds = async (ids: ArtifactIdentifier[]) => {
    const missed = ids.filter(id => !artifacts[id]);
    const missedIds = missed.map(String);

    let missedArtifacts: ArtifactMap = {};
    if (missedIds.length) {
      missedArtifacts = await collectArtifactsDataAction(missedIds);
    }

    setArtifactsToStore(missedArtifacts);

    return (
      ids
        .map(id => artifacts[id] || missedArtifacts[id])
        .map(a => Object.freeze(a))
        .filter(file => file !== undefined) || []
    );
  };

  const getInvoiceArtifactsDataByIds = async (invoiceId: ArtifactIdentifier, artifactIds: ArtifactIdentifier[]) => {
    const missed = artifactIds.filter(id => !artifacts[id]);
    const missedIds = missed.map(String);

    let missedArtifacts: ArtifactMap = {};
    if (missedIds.length) {
      missedArtifacts = await collectInvoiceArtifactsData(String(invoiceId), missedIds);
    }

    setArtifactsToStore(missedArtifacts);

    return (
      artifactIds
        .map(id => artifacts[id] || missedArtifacts[id])
        .map(a => Object.freeze(a))
        .filter(file => file !== undefined) || []
    );
  };

  const getArtifactDataById = async (artifactId: ArtifactIdentifier) => {
    const result = await getArtifactsDataByIds([artifactId]);
    return result[0];
  };

  const clearArtifactsFromStore = () => dispatch(clearArtifacts());

  const getArtifactIcon = (artifact: Pick<ArtifactData, 'type'>) => getFileTypeIcon(artifact.type);

  const downloadArtifact = async (id: ArtifactIdentifier) => {
    const response = (await (sendGetRequest as SendGetRequest)(
      buildArtifactUrl(id, 'file/content'),
      HTTP_METHOD.GET,
      null,
      null,
      null,
      [{ name: 'responseType', value: 'blob' }],
    )) as BlobResponse;
    return response.blob();
  };

  const downloadInvoiceArtifact = async (id: ArtifactIdentifier) => {
    const response = (await financeGetRequest(
      `/invoices/${id}/artifacts/file/content`,
      HTTP_METHOD.GET,
      null,
      null,
      null,
      [{ name: 'responseType', value: 'blob' }],
    )) as BlobResponse;

    return response.blob();
  };

  const downloadArtifactPreview = async (id: ArtifactIdentifier) => {
    const response = (await (sendGetRequest as SendGetRequest)(
      buildArtifactUrl(id, 'preview/content'),
      HTTP_METHOD.GET,
      null,
      null,
      null,
      [{ name: 'responseType', value: 'blob' }],
    )) as BlobResponse;
    return response.blob();
  };

  const downloadInvoiceArtifactPreview = async (id: ArtifactIdentifier) => {
    const response = (await financeGetRequest(
      `/invoices/${id}/artifacts/preview/content`,
      HTTP_METHOD.GET,
      null,
      null,
      null,
      [{ name: 'responseType', value: 'blob' }],
    )) as BlobResponse;

    return response.blob();
  };

  const getArtifactThumbnailsFromApi = async (id: ArtifactIdentifier) => {
    const artifactsObjects = (await collectArtifactsDataAction([String(id)])) as ArtifactMap;

    setArtifactsToStore(artifactsObjects);

    return (artifactsObjects[id] && artifactsObjects[id].thumbnails) || {};
  };

  const getInvoiceArtifactThumbnailsFromApi = async (invoiceId: ArtifactIdentifier, artifactId: ArtifactIdentifier) => {
    const artifactsObjects = (await collectInvoiceArtifactsData(String(invoiceId), [
      String(artifactId),
    ])) as ArtifactMap;

    setArtifactsToStore(artifactsObjects);

    return (artifactsObjects[artifactId] && artifactsObjects[artifactId].thumbnails) || {};
  };

  const getThumbnail = async (id: ArtifactIdentifier, cropSize: string) => {
    if (!id) {
      return null;
    }

    const artifact = await getArtifactDataById(id);

    const url = artifact && artifact.thumbnails && artifact.thumbnails[cropSize];

    if (!url || isLinkExpired(url)) {
      const thumbnails = await getArtifactThumbnailsFromApi(id);
      return thumbnails[cropSize];
    } else {
      return url;
    }
  };

  const getInvoiceThumbnail = async (
    invoiceId: ArtifactIdentifier,
    artifactId: ArtifactIdentifier,
    cropSize: string,
  ) => {
    if (!artifactId) {
      return null;
    }

    const artifact = await getArtifactDataById(artifactId);

    const url = artifact && artifact.thumbnails && artifact.thumbnails[cropSize];

    if (!url || isLinkExpired(url)) {
      const thumbnails = await getInvoiceArtifactThumbnailsFromApi(invoiceId, artifactId);
      return thumbnails[cropSize];
    } else {
      return url;
    }
  };

  const { findAll: findAllFromApi } = useBaseCrudService({
    listResourceUrl: '/artifact-types',
    singleResourceUrl: '',
    useService: useArtifactStorageApi,
  });

  const { getDataFromStorage: getArtifactTypeObjects } = storageService('artifactTypesObject', findAllFromApi, '1.7');

  const getArtifactTypeObject = async (artifactTypeId: string | number) => {
    const artifactTypesObject = await getArtifactTypeObjects();

    return artifactTypesObject && artifactTypesObject.find((obj: { id: string | number }) => obj.id === artifactTypeId);
  };

  const uploadArtifact = async ({
    file,
    signal,
    artifactTypeId,
    onProgress,
  }: {
    file: File;
    signal?: AbortSignal;
    artifactTypeId: string | number | Blob;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  }) => {
    const { response } = await (uploadFileFromProps || uploadFile)({
      url: uploadUrl,
      data: mapToFileData({ file, artifactTypeId }),
      eventTarget: signal,
      onProgress,
    });

    const data = JSON.parse(response) as { id: string; type: string };
    const { id, type } = data;

    const filePreview = await getFilePreview(file);

    setArtifactsToStore({
      [id]: {
        filePreview,
        id,
        type,
        name: file.name,
        title: file.name,
      },
    });

    return id || null;
  };

  return {
    getArtifactIcon,
    getArtifactsDataByIds,
    getInvoiceArtifactsDataByIds,
    getArtifactDataById,
    downloadArtifact,
    downloadInvoiceArtifact,
    downloadArtifactPreview,
    downloadInvoiceArtifactPreview,
    setArtifactsToStore,
    removeArtifactsFromStore,
    clearArtifactsFromStore,
    uploadArtifact,
    getArtifactTypeObject,
    getThumbnail,
    getInvoiceThumbnail,
    getById,
    getPermissions,
    isArtifactsInfoReceiverLoading,
  };
};

export default useArtifactService;
